import React, { useState, useEffect } from "react";
import axios from "axios";
import { useParams, Link } from "react-router-dom";
import foodImage from '../img/fondoAlmuerzo.jpeg';
import dinnerImage from '../img/fondocena.jpg';

const apiUrlMenuSemanals = process.env.REACT_APP_API_MENUSEMANALS;

const parseLocalDate = (fechaStr) => {
  if (!fechaStr) return null;
  const datePart = fechaStr.split('T')[0];
  const parts = datePart.split('-').map(p => Number(p));
  if (parts.length < 3 || parts.some(isNaN)) return null;
  const [y, m, d] = parts;
  return new Date(y, m - 1, d);
};

const getTipoLabel = (item) => {
  if (item?.bebida_id) return item?.bebida?.tipo || item?.tipo || 'Bebida';
  return item?.plato?.tipoplato?.nombre || item?.tipoplato?.nombre || item?.tipo || 'Sin clasificar';
};

const SoloVerMenus = ({ isPublic = false }) => {
  const { id_sede } = useParams();
  const [platos, setPlatos] = useState([]);
  const [error, setError] = useState(null);
  const [selectedMeal, setSelectedMeal] = useState('both');
  const [mobileAnimating, setMobileAnimating] = useState(false);

  // Priorizar id_sede de URL (QR), fallback a localStorage si no es público
  const sedeUsuario = id_sede || (!isPublic ? localStorage.getItem("id_sede") : null);

  // Si estamos en pantalla pequeña, por defecto mostrar almuerzo
  useEffect(() => {
    try {
      if (window && window.innerWidth && window.innerWidth < 768) {
        setSelectedMeal('almuerzo');
      }
    } catch (e) {
      // ignore (SSR guard)
    }
  }, []);

  const handleMealChange = (value) => {
    // small animation on mobile: fade + translate
    setMobileAnimating(true);
    // delay the actual change so animation is perceived
    setTimeout(() => {
      setSelectedMeal(value);
      setMobileAnimating(false);
    }, 260);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(apiUrlMenuSemanals);
        const platosData = response.data;
        // Filtrar estrictamente por id_sede del usuario si está presente
        let platosFiltrados = sedeUsuario
          ? platosData.filter(plato => plato.id_sede != null && String(plato.id_sede) === String(sedeUsuario))
          : platosData;

        // Mostrar solo los menús de la semana próxima (lunes a domingo)
        const hoy = new Date();
        const diaSemana = hoy.getDay(); // 0=domingo,1=lunes...
        const lunesActual = new Date(hoy);
        lunesActual.setDate(hoy.getDate() - ((diaSemana + 6) % 7));
        lunesActual.setHours(0, 0, 0, 0);
        const lunesProx = new Date(lunesActual);
        lunesProx.setDate(lunesActual.getDate() + 7);
        lunesProx.setHours(0, 0, 0, 0);
        const domingoProx = new Date(lunesProx);
        domingoProx.setDate(lunesProx.getDate() + 6);
        domingoProx.setHours(23, 59, 59, 999);

        const parseDate = (val) => {
          if (!val) return null;
          if (typeof val === 'string') {
            const match = val.match(/\d{4}-\d{2}-\d{2}/);
            if (match) return parseLocalDate(match[0]); // use local-date parser to avoid timezone shifts
            const d = new Date(val);
            if (!isNaN(d)) return d;
            const n = Number(val);
            if (!isNaN(n)) return new Date(n);
            return null;
          }
          if (typeof val === 'number') return new Date(val);
          if (val instanceof Date) return val;
          return null;
        };

        platosFiltrados = platosFiltrados.filter(p => {
          const fecha = parseDate(p.fecha);
          if (!fecha) return false;
          return fecha >= lunesProx && fecha <= domingoProx;
        });

        setPlatos(platosFiltrados);
      } catch (error) {
        setError("Error al obtener los menús seleccionados.");
      }
    };
    fetchData();
  }, [sedeUsuario]);

  // Agrupar platos por comida (almuerzo/cena) y por fecha
  const agruparPorComidaYFecha = (items) => {
    const res = { almuerzo: {}, cena: {} };
    items.forEach(p => {
      const comida = (p.comida || 'almuerzo').toLowerCase();
      const fechaStr = (p.fecha || '').toString().split('T')[0] || 'Sin fecha';
      if (!res[comida]) res[comida] = {};
      if (!res[comida][fechaStr]) res[comida][fechaStr] = [];
      res[comida][fechaStr].push(p);
    });
    return res;
  };

  const agrupados = agruparPorComidaYFecha(platos);

  const renderCol = (titulo, data, image) => {
    const fechas = Object.keys(data).sort((a, b) => new Date(a) - new Date(b));
    const totalMenus = fechas.reduce((sum, f) => sum + (data[f]?.length || 0), 0);

    return (
      <div>
        <div
          className="relative rounded-xl overflow-hidden mb-4 border-2 border-cyan-300 shadow-lg h-24 sm:h-32 md:h-40"
          style={{ backgroundImage: `url(${image})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-black/50 to-black/30" />
          <div className="relative z-10 flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4">
            <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-white drop-shadow-lg">{titulo}</h3>
            <span className="text-sm sm:text-base bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full text-white font-semibold">Total: {totalMenus}</span>
          </div>
        </div>

        <div className="space-y-4">
          {fechas.length === 0 && (
            <div className="bg-white rounded-xl shadow-md p-6 text-center text-gray-500">No hay menús para esta categoría.</div>
          )}

          {fechas.map((fecha, idx) => {
            const platosDia = data[fecha];
            const d = parseLocalDate(fecha);
            const fechaLabel = d ? d.toLocaleDateString('es-AR', { weekday: 'long', day: 'numeric', month: 'short' }) : fecha;
            const highlight = idx === 0;

            // Separar platos de bebidas
            const platosSinBebidas = platosDia.filter(p => !p.bebida_id);
            const bebidas = platosDia.filter(p => p.bebida_id);

            return (
              <div key={fecha} className={`bg-white rounded-xl shadow-lg border-2 p-4 sm:p-5 ${highlight ? 'border-cyan-500 ring-2 ring-cyan-200' : 'border-gray-200'}`}>
                <div className="flex items-center justify-between mb-3 pb-3 border-b-2 border-gray-100">
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">📅</span>
                    <div className="text-sm sm:text-base text-gray-800 font-bold capitalize">{fechaLabel}</div>
                  </div>
                  <div className="text-xs sm:text-sm bg-gradient-to-r from-cyan-500 to-teal-600 text-white px-3 py-1 rounded-full font-semibold">{platosDia.length} menú{platosDia.length !== 1 ? 's' : ''}</div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                  {/* Columna izquierda: Platos */}
                  <div>
                    {platosSinBebidas.length > 0 && (
                      <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-lg border-2 border-orange-200 p-3">
                        <h4 className="text-sm font-bold text-orange-700 mb-3 flex items-center gap-2">
                          🍽️ Platos
                        </h4>
                        <div className="space-y-2">
                          {platosSinBebidas.map((plato, i) => (
                            <div key={i} className="bg-white rounded-lg p-3 sm:p-4 border border-orange-200 hover:shadow-md hover:border-orange-300 transition-all">
                              <div className="font-semibold text-gray-800 mb-1 text-sm sm:text-base">{plato.nombre}</div>
                              <div className="text-xs sm:text-sm text-gray-600">{getTipoLabel(plato)}</div>
                              {plato.nombreuser && (
                                <div className="text-xs sm:text-sm text-gray-500 mt-1">
                                  {plato.nombreuser} {plato.apellido || ''}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Columna derecha: Bebidas */}
                  <div>
                    {bebidas.length > 0 && (
                      <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-lg border-2 border-blue-200 p-3">
                        <h4 className="text-sm font-bold text-blue-700 mb-3 flex items-center gap-2">
                          🥤 Bebidas
                        </h4>
                        <div className="space-y-2">
                          {bebidas.map((plato, i) => (
                            <div key={i} className="bg-white rounded-lg p-3 sm:p-4 border border-blue-200 hover:shadow-md hover:border-blue-300 transition-all">
                              <div className="font-semibold text-gray-800 mb-1 text-sm sm:text-base">{plato.nombre}</div>
                              {getTipoLabel(plato) && (
                                <div className="text-xs sm:text-sm text-gray-600">{getTipoLabel(plato)}</div>
                              )}
                              {plato.nombreuser && (
                                <div className="text-xs sm:text-sm text-gray-500 mt-1">
                                  {plato.nombreuser} {plato.apellido || ''}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-teal-50 to-blue-50 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-6 border-b-4 border-cyan-500">
          <div className="flex items-center gap-4 mb-4">
            <div className="bg-gradient-to-br from-cyan-500 to-teal-600 p-4 rounded-xl shadow-lg">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
              </svg>
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-800">Menús de la Semana</h1>
              <p className="text-gray-600 mt-1">Consulta los menús disponibles para la próxima semana</p>
            </div>
          </div>
          <div className="bg-gradient-to-r from-cyan-50 to-teal-50 rounded-xl p-4 border-2 border-cyan-200">
            <p className="text-sm text-gray-700 text-center">
              📅 Mostrando menús de <span className="font-semibold">Lunes a Domingo</span> de la semana próxima
            </p>
          </div>
        </div>
        {/* Mobile selector */}
        <div className="mb-6 md:hidden">
          <label className="sr-only" htmlFor="meal-select">Seleccionar comida</label>
          <select
            id="meal-select"
            value={selectedMeal}
            onChange={(e) => handleMealChange(e.target.value)}
            className="w-full rounded-lg bg-white text-gray-800 p-3 border-2 border-cyan-300 font-medium shadow-md focus:border-cyan-500 focus:ring-2 focus:ring-cyan-200 transition"
          >
            <option value="almuerzo">🌅 Almuerzo</option>
            <option value="cena">🌙 Cena</option>
            <option value="both">🍽️ Ambas comidas</option>
          </select>
        </div>
        {error && (
          <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-r-lg shadow-md mb-6">
            <div className="flex items-start gap-3">
              <svg className="w-6 h-6 text-red-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        )}
        <div className={`grid grid-cols-1 lg:grid-cols-2 gap-6 transition-transform transition-opacity duration-300 ease-out ${mobileAnimating ? 'opacity-60 translate-y-2' : 'opacity-100 translate-y-0'}`}>
          {(selectedMeal === 'both' || selectedMeal === 'almuerzo') && (
            <div>
              {renderCol('🌅 Almuerzo', agrupados.almuerzo, foodImage)}
            </div>
          )}

          {(selectedMeal === 'both' || selectedMeal === 'cena') && (
            <div>
              {renderCol('🌙 Cena', agrupados.cena, dinnerImage)}
            </div>
          )}
        </div>

        {isPublic && (
          <div className="mt-12 flex justify-center">
            <Link
              to="/"
              className="px-8 py-4 bg-gradient-to-r from-cyan-500 to-teal-600 hover:from-cyan-600 hover:to-teal-700 text-white font-bold rounded-xl shadow-lg transition-all transform hover:scale-105 flex items-center gap-3"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
              Regresar al Inicio/Login
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default SoloVerMenus;
